import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodePartnerAdminsUsersKey,
  getGetApiV1ThreeLetterCodePartnerAdminsUsersUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodePartnerAdminsUsers200,
  GetApiV1ThreeLetterCodePartnerAdminsUsersParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

const fetcher = (
  threeLetterCode: string,
  apiParams: GetApiV1ThreeLetterCodePartnerAdminsUsersParams
) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodePartnerAdminsUsers200>(
    getGetApiV1ThreeLetterCodePartnerAdminsUsersUrl(threeLetterCode, apiParams),
    { method: 'GET' }
  );

type Args = {
  /** パートナーの 3 文字コード */
  threeLetterCode: string;
  /** 検索フィルター */
  filter?: {
    /** 氏名（部分一致） */
    fullName?: string;
    /** メールアドレス（部分一致） */
    email?: string;
    /** 会社名（部分一致） */
    clientName?: string;
    /** 電話番号（部分一致） */
    phoneNumber?: string;
    /** アクティベーションコード（完全一致） */
    activationCode?: string;
  };
  /** ページ番号（1始まりの文字列） */
  page?: string;
};

type IUseGetPartnerAdminsUsers = (args: Args) => {
  data: GetApiV1ThreeLetterCodePartnerAdminsUsers200;
  totalPage: number;
  totalCount: number;
  mutate: KeyedMutator<ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsUsers200>>;
};

/**
 * パートナー管理者向け利用者一覧取得フック
 *
 * - ページネーション対応（total-pages / total-count はレスポンスヘッダーから取得）
 */
export const useGetPartnerAdminsUsers: IUseGetPartnerAdminsUsers = ({
  threeLetterCode,
  filter,
  page,
}) => {
  const apiParams: GetApiV1ThreeLetterCodePartnerAdminsUsersParams = {
    ...(filter?.fullName && { full_name: filter.fullName }),
    ...(filter?.email && { email: filter.email }),
    ...(filter?.clientName && { client_name: filter.clientName }),
    ...(filter?.phoneNumber && { phone_number: filter.phoneNumber }),
    ...(filter?.activationCode && { activation_code: filter.activationCode }),
    ...(page && !Number.isNaN(Number(page)) && { page: Number(page) }),
  };

  const { data, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsUsers200>
  >(
    getGetApiV1ThreeLetterCodePartnerAdminsUsersKey(threeLetterCode, apiParams),
    () => fetcher(threeLetterCode, apiParams),
    { suspense: true }
  );

  const totalPage = Number(data.headers['total-pages']) || 0;
  const totalCount = Number(data.headers['total-count']) || 0;

  return {
    data: data.data,
    totalPage,
    totalCount,
    mutate,
  };
};
